import { boxPlanningKeys } from "@/modules/boxPlanning/gateway/boxPlanningKeys"
import { boxSlotKeys } from "@/modules/boxSlot/gateway/boxSlotKeys"
import { useServicesContext } from "@/modules/core/services/services.context"
import { ParticipantStatus } from "@/modules/profile/subscriber/subscriberTypes"
import { UserEntity } from "@/modules/profile/userProfile/userEntity"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

export function useBoxSlotParticipantAbsence() {
  const { boxSlotApi } = useServicesContext()
  return useMutation({
    mutationFn: async ({ slotId, memberId, absent }: { slotId: string; memberId: string; absent: boolean }) => {
      return await boxSlotApi.setParticipantAbsence(slotId, memberId, absent)
    },
  })
}

export function useBoxSlotAddParticipant() {
  const { boxSlotApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      slotId,
      memberId,
    }: {
      slotId: string
      memberId: string
      participantStatus?: ParticipantStatus
    }) => {
      return await boxSlotApi.addParticipantToSlot(slotId, memberId)
    },
    onSuccess: async (_, { slotId, participantStatus }) => {
      if (participantStatus === ParticipantStatus.PENDING) {
        await queryClient.refetchQueries({
          queryKey: boxSlotKeys.subscribers(slotId, ParticipantStatus.REGISTERED),
        })
        await queryClient.refetchQueries({
          queryKey: boxSlotKeys.subscribers(slotId, ParticipantStatus.PENDING),
        })
      } else if (participantStatus === undefined) {
        await queryClient.refetchQueries({
          queryKey: boxSlotKeys.subscribers(slotId, ParticipantStatus.REGISTERED),
        })
      }

      await queryClient.refetchQueries({
        queryKey: boxPlanningKeys.slots(),
      })
    },
  })
}

export function useBoxSlotRemoveParticipant() {
  const { boxSlotApi } = useServicesContext()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      slotId,
      memberId,
    }: {
      slotId: string
      memberId: string
      participantStatus?: ParticipantStatus
    }) => {
      return await boxSlotApi.removeParticipantToSlot(slotId, memberId)
    },
    onSuccess: async (_, { slotId, participantStatus }) => {
      await queryClient.refetchQueries({
        queryKey: boxSlotKeys.subscribers(slotId, ParticipantStatus.PENDING),
      })
      if (participantStatus === ParticipantStatus.REGISTERED) {
        await queryClient.refetchQueries({
          queryKey: boxSlotKeys.subscribers(slotId, ParticipantStatus.REGISTERED),
        })
        await queryClient.refetchQueries({
          queryKey: boxPlanningKeys.slots(),
        })
      }
    },
  })
}

export function useBoxSlotGetNonParticipants({ slotId, name }: { slotId: string; name?: string }) {
  const { boxSlotApi } = useServicesContext()

  return useQuery({
    queryKey: boxSlotKeys.nonParticipants(slotId, { name }),
    queryFn: async () => {
      const result = await boxSlotApi.getSlotNonParticipants(slotId, name)
      return { ...result, content: result.content.map((member) => UserEntity.fromDto(member)) }
    },
  })
}
